import { useCallback, useMemo } from 'react';
import {
  useUserPreference,
  useUpsertUserPreference,
} from '@/api/rpc/hooks/core/useUserPreference';
import {
  SidebarPreferencesSchema,
  type SidebarNavLayout,
  type SidebarPreferences,
} from '@erp/rpc';

const DEFAULTS: SidebarPreferences = {
  collapsedGroups: [],
  hiddenItems: [],
};

export function useSidebarPreferences() {
  const { data, isLoading } = useUserPreference('sidebar');
  const { mutate } = useUpsertUserPreference();

  const prefs = useMemo<SidebarPreferences>(() => {
    if (!data) return DEFAULTS;
    // Parse through the schema so optional fields (and newly-added ones like
    // layout.unusedItemIds) get their defaults applied for older stored values.
    const parsed = SidebarPreferencesSchema.safeParse(data.value);
    return parsed.success ? parsed.data : DEFAULTS;
  }, [data]);

  const persist = useCallback(
    (next: SidebarPreferences) => {
      mutate({ key: 'sidebar', value: next });
    },
    [mutate],
  );

  const toggleGroup = useCallback(
    (group: string) => {
      const collapsed = prefs.collapsedGroups.includes(group)
        ? prefs.collapsedGroups.filter((g) => g !== group)
        : [...prefs.collapsedGroups, group];
      persist({ ...prefs, collapsedGroups: collapsed });
    },
    [prefs, persist],
  );

  const toggleItem = useCallback(
    (itemId: string) => {
      const hidden = prefs.hiddenItems.includes(itemId)
        ? prefs.hiddenItems.filter((i) => i !== itemId)
        : [...prefs.hiddenItems, itemId];
      persist({ ...prefs, hiddenItems: hidden });
    },
    [prefs, persist],
  );

  const setLayout = useCallback(
    (layout: SidebarNavLayout) => {
      persist({ ...prefs, layout });
    },
    [prefs, persist],
  );

  const clearLayout = useCallback(() => {
    const { layout: _, ...rest } = prefs;
    persist(rest);
  }, [prefs, persist]);

  return {
    collapsedGroups: prefs.collapsedGroups,
    hiddenItems: prefs.hiddenItems,
    layout: prefs.layout,
    toggleGroup,
    toggleItem,
    setLayout,
    clearLayout,
    isLoading,
  };
}
